import { Component } from '@angular/core';
import { PatientsService } from '../../../../core/services/patient/patients.service';
import { CommonModule, DatePipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CurrentUserService } from '../../../../core/services/user/current-user.service';
import { CustomersService } from '../../../../core/services/customer/customers.service';
import { StatusUtils } from '../../../../utils/status-utils';
import { environment } from '../../../../../environments/environment';
import { SignalRService } from '../../../../core/services/signalR/signalR';
import { PatientListModel } from '../../../../core/models/patient/patient-list.model';
import { UserSessionModel } from '../../../../core/models/user/current-user.model';
import { CustomerListModel } from '../../../../core/models/customer/customer-list.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablesComponent } from '../../../components/tables/tables/tables.component';
import { ColumnModel } from '../../../../core/models/table/column.model';
import { FilterModel } from '../../../../core/models/filters/filters.model';
import { SortModel } from '../../../../core/models/filters/sort.model';
import { DateFormatterUtil } from '../../../../utils/date-format-utils';
@Component({
  selector: 'app-patient-list',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    TranslateModule,
    TablesComponent,
  ],
  templateUrl: './patient-list.component.html',
  styleUrl: './patient-list.component.scss',
  providers: [DatePipe],
})
export class PatientListComponent {
  currentUser: UserSessionModel | null = null;
  patients: PatientListModel[] = [];
  totalPatient: number = 0;
  totalPage: number = 0;
  pageSize: number = 10;
  pageSizes = [10, 25, 50, 100];
  currentPage: number = 1;
  patientListLoading: boolean = true;
  filterForm = {
    pid: '',
    nameSurname: '',
    startCreatedDate: null as string | null,
    endCreatedDate: null as string | null,
    customerCode: null as any,
    createdBy: '',
    status: null as any,
  };
  organizationList: CustomerListModel[] = [];
  caseStatuses = StatusUtils.getFilterCaseStatuses();
  maxDate: string;
  filters: FilterModel[] = [
    //Derm No
    {
      type: 'input',
      label: 'agbCode',
      placeholder: 'filterByAgbCode',
      filterId: 'PIDNo',
      value: null,
    },
    //StartDate
    {
      type: 'date',
      label: 'startCreatedDate',
      placeholder: 'dd-mm-yyyy',
      filterId: 'StartCreatedDateTime',
      value: null,
    },
    //EndCreateDate
    {
      type: 'date',
      label: 'endCreatedDate',
      placeholder: 'dd-mm-yyyy',
      filterId: 'EndCreatedDateTime',
      value: null,
    },
    //PatientName
    {
      type: 'input',
      label: 'patientName',
      placeholder: 'filterByNameAndSurname',
      filterId: 'NameSurname',
      value: null,
    },
    //Status
    {
      type: 'select',
      label: 'status',
      placeholder: 'selectStatus',
      filterId: 'Status',
      value: null,
    },
  ];
  sort: SortModel | null = null;
  tableHeader: string = '';
  footerText: string = '';
  columns: ColumnModel[] = [];
  columnWidths: string[] = [
    'w-125',
    'w-200',
    'w-250',
    'w-125',
    'w-175',
    'w-250',
  ];

  constructor(
    private currentUserService: CurrentUserService,
    private patientsService: PatientsService,
    private customersService: CustomersService,
    private signalRService: SignalRService,
    private translate: TranslateService
  ) {
    const userLang = localStorage.getItem('selectedLanguage') || 'en';
    this.translate.use(userLang);
    this.createPatientListSignalRConnection();
    const today = new Date();
    this.maxDate = today.toISOString().split('T')[0];

    this.columns = [
      { key: 'pidNo', translateKey: 'agbCode' },
      { key: 'createdDate', translateKey: 'createdDate' },
      { key: 'nameAndSurname', translateKey: 'patientName' },
      { key: 'customerCode', translateKey: 'customerCode' },
      { key: 'createdBy', translateKey: 'createdBy' },
      { key: 'patientStatus', translateKey: 'status' },
    ];

    setTimeout(() => {
      this.tableHeader = this.translate.instant('patientList');
      this.footerText = this.translate.instant('footerText.patients');
    }, 100);
  }

  ngOnInit(): void {
    this.currentUser = this.currentUserService.GetSession();
    this.getPatientList(this.currentPage, this.pageSize);
    this.getOrganizations();
  }

  createPatientListSignalRConnection() {
    const signalRBasePath = `${environment.apiLocations.patient}`.split(
      '/api/v1'
    )[0];
    const hubUrl = signalRBasePath + '/patient-hub';
    this.signalRService.connect(hubUrl);
    this.signalRService.on('receivePatientCreated', (message) => {
      const parsedPatient = JSON.parse(message);
      if (parsedPatient) {
        const newPatient = {
          pidNo: parsedPatient.PIDNo,
          createdDate: parsedPatient.CreatedDate,
          name: parsedPatient.Name,
          lastName: parsedPatient.LastName,
          customerCode: parsedPatient.CustomerCode,
          createdBy: parsedPatient.CreatedBy,
          patientStatus: parsedPatient.PatientStatus,
          patientId: parsedPatient.PatientId,
        };
        this.totalPatient++;
        this.patients = [newPatient, ...this.patients];
      }
    });
  }

  filterPatientList(filters?: FilterModel[], sort?: SortModel) {
    if (filters && sort === null) {
      const dateFilters = filters.filter(
        (filter) =>
          (filter.filterId === 'StartCreatedDateTime' ||
            filter.filterId === 'EndCreatedDateTime') &&
          filter.value
      );

      dateFilters.forEach((filter) => {
        filter.value = DateFormatterUtil.formatDate(filter.value);
      });

      this.getPatientList(1, this.pageSize, filters);
    } else if (sort && filters.every((filter) => filter.value === null)) {
      this.sort = sort;
      sort.column = this.getSortByValue(sort.column);
      this.getPatientList(this.currentPage, this.pageSize, null, sort);
    } else {
      this.getPatientList(this.currentPage, this.pageSize, filters, sort);
    }
  }

  getSortByValue(column: string): string {
    switch (column) {
      case 'pidNo':
        return 'PIDNo';
      case 'nameAndSurname':
        return 'Name';
      case 'customerCode':
        return 'PatientRecord.CustomerCode';
      case 'createdBy':
        return 'CreatedUser';
      case 'patientStatus':
        return 'PatientRecord.PatientRecordCases.PatientStatus';
      default:
        return column;
    }
  }

  getPatientList(
    page: number,
    size: number,
    filters?: FilterModel[],
    sort?: SortModel
  ): void {
    this.patientListLoading = true;
    this.patientsService.patientList(page, size, filters, sort).subscribe({
      next: (res) => {
        this.patients = res?.items || [];
        this.totalPatient = res?.count || 0;
        this.totalPage = res?.pages || 0;
        this.pageSize = res?.size || 0;
        this.currentPage = page;
        this.patientListLoading = false;
      },
    });
  }

  clearFilters() {
    this.filterForm.nameSurname = '';
    this.filterForm.pid = '';
    this.filterForm.status = null;
    this.filterForm.startCreatedDate = null;
    this.filterForm.endCreatedDate = null;
    this.filterForm.customerCode = null;
    this.filterForm.createdBy = '';

    this.getPatientList(this.currentPage, this.pageSize);
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.getPatientList(
      this.currentPage,
      this.pageSize,
      this.filters,
      this.sort ? this.sort : null
    );
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.getPatientList(
      this.currentPage,
      this.pageSize,
      this.filters,
      this.sort ? this.sort : null
    );
  }

  getStatusClass(status: number): string {
    return StatusUtils.getCaseStatusClass(status);
  }

  getStatusText(status: number): string {
    return StatusUtils.getCaseStatusText(status);
  }

  getOrganizations() {
    this.customersService.allCustomerCodes().subscribe({
      next: (res) => {
        this.organizationList = res;
      },
    });
  }
}
